import { cn } from '@/shared/lib/utils'
import { ComponentProps, ElementType } from 'react'

export type PolymorphicProps<E extends ElementType> = {
  as?: E
} & Omit<ComponentProps<E>, 'as'>

// Typography variants with their styles and default elements
const typographyVariants = {
  h1: {
    className:
      'font-gt-planar text-[1.75rem] leading-[2rem] tracking-[-2%] md:text-[2.5rem] md:leading-[2.75rem]',
    defaultElement: 'h1' as const,
  },
  h2: {
    className:
      'font-sans text-[1.625rem] leading-[1.875rem] tracking-[-0.0325rem] md:text-[1.875rem] md:leading-[2.25rem] md:tracking-[-0.0375rem]',
    defaultElement: 'h2' as const,
  },
  h3: {
    className:
      'font-gt-planar text-[1.375rem] leading-[1.625rem] tracking-[-0.03rem] md:text-[1.625rem] md:leading-[2rem] md:tracking-[-0.035rem]',
    defaultElement: 'h3' as const,
  },
  h4: {
    className:
      'font-sans text-[1.125rem] leading-[1.875rem] tracking-[-0.0225rem] md:text-[1.375rem] md:tracking-[-0.0275rem]',
    defaultElement: 'h4' as const,
  },
  h5: {
    className: 'font-gt-planar text-[1.125rem] leading-[1.5rem]',
    defaultElement: 'h5' as const,
  },
  subtitle: {
    className:
      'font-sans text-sm leading-[142.857%] font-regular md:text-lg md:leading-[122.22%]',
    defaultElement: 'p' as const,
  },
  body1: {
    className: 'font-sans text-[1rem] leading-[1.5rem]',
    defaultElement: 'p' as const,
  },
  body2: {
    className:
      'font-sans text-[0.875rem] leading-[1.25rem] tracking-[-0.015rem]',
    defaultElement: 'p' as const,
  },
  caption: {
    className: 'font-sans text-[0.75rem] leading-[1rem]',
    defaultElement: 'p' as const,
  },
} as const

export type TypographyVariant = keyof typeof typographyVariants

export type TypographyProps<E extends ElementType = 'p'> = {
  variant?: TypographyVariant
  as?: E
  className?: string
} & Omit<ComponentProps<E>, 'as' | 'className'>

export function Typography<E extends ElementType = 'p'>({
  variant = 'body1',
  as,
  className,
  children,
  ...props
}: TypographyProps<E>) {
  const variantConfig = typographyVariants[variant]
  const Component = (as || variantConfig.defaultElement) as ElementType

  return (
    <Component className={cn(variantConfig.className, className)} {...props}>
      {children}
    </Component>
  )
}

// Individual components for backward compatibility and direct usage
function H1({
  children,
  as,
  className,
  ...props
}: PolymorphicProps<'h1'> & { className?: string }) {
  return (
    <Typography variant="h1" as={as || 'h1'} className={className} {...props}>
      {children}
    </Typography>
  )
}

function H2({
  children,
  as,
  className,
  ...props
}: PolymorphicProps<'h2'> & { className?: string }) {
  return (
    <Typography variant="h2" as={as || 'h2'} className={className} {...props}>
      {children}
    </Typography>
  )
}

function H3({
  children,
  as,
  className,
  ...props
}: PolymorphicProps<'h3'> & { className?: string }) {
  return (
    <Typography variant="h3" as={as || 'h3'} className={className} {...props}>
      {children}
    </Typography>
  )
}

function H4({
  children,
  as,
  className,
  ...props
}: PolymorphicProps<'h4'> & { className?: string }) {
  return (
    <Typography variant="h4" as={as || 'h4'} className={className} {...props}>
      {children}
    </Typography>
  )
}

function H5({
  children,
  as,
  className,
  ...props
}: PolymorphicProps<'h5'> & { className?: string }) {
  return (
    <Typography variant="h5" as={as || 'h5'} className={className} {...props}>
      {children}
    </Typography>
  )
}

function Subtitle({
  children,
  as,
  className,
  ...props
}: PolymorphicProps<'p'> & { className?: string }) {
  return (
    <Typography
      variant="subtitle"
      as={as || 'p'}
      className={className}
      {...props}
    >
      {children}
    </Typography>
  )
}

function Body({
  children,
  as,
  className,
  ...props
}: PolymorphicProps<'p'> & { className?: string }) {
  return (
    <Typography variant="body1" as={as || 'p'} className={className} {...props}>
      {children}
    </Typography>
  )
}

function Body2({
  children,
  as,
  className,
  ...props
}: PolymorphicProps<'p'> & { className?: string }) {
  return (
    <Typography variant="body2" as={as || 'p'} className={className} {...props}>
      {children}
    </Typography>
  )
}

function Caption({
  children,
  as,
  className,
  ...props
}: PolymorphicProps<'p'> & { className?: string }) {
  return (
    <Typography
      variant="caption"
      as={as || 'p'}
      className={className}
      {...props}
    >
      {children}
    </Typography>
  )
}

// Export individual components and the unified Typography component
export { Body, Body2, Caption, H1, H2, H3, H4, H5, Subtitle }
